import { flexRender, getCoreRowModel, useReactTable, type ColumnDef, type ColumnSort } from '@tanstack/react-table'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils'
import { EmptyState } from './EmptyState'
import { Spinner } from './Spinner'
import type { PaginationState } from '@/shared/types'

interface Props<T> {
  columns: ColumnDef<T>[]
  data: T[]
  isLoading?: boolean
  pagination?: PaginationState & { total: number; last_page: number }
  onPaginationChange?: (p: PaginationState) => void
  sorting?: ColumnSort | null
  onSortingChange?: (s: ColumnSort) => void
  emptyMessage?: string
  className?: string
  onRowClick?: (row: T) => void
}

export function DataTable<T>({ columns, data, isLoading, pagination, onPaginationChange, sorting, onSortingChange, emptyMessage, className, onRowClick }: Props<T>) {
  const { t } = useTranslation()
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel(), manualPagination: true, manualSorting: true })

  return (
    <div className={cn('', className)}>
      <div className="overflow-x-auto">
        <table className="drivys-table w-full">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(h => (
                  <th key={h.id} onClick={() => {
                    if (h.column.getCanSort() && onSortingChange) {
                      onSortingChange({ id: h.id, desc: sorting?.id === h.id ? !sorting.desc : false })
                    }
                  }}>
                    <div className="flex items-center gap-1">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {h.column.getCanSort() && (
                        sorting?.id === h.id ? (sorting.desc ? <ChevronDown size={12}/> : <ChevronUp size={12}/>) : <ChevronsUpDown size={12} className="text-gray-300"/>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={columns.length}><div className="flex justify-center py-12"><Spinner/></div></td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={columns.length}><EmptyState message={emptyMessage}/></td></tr>
            ) : (
              table.getRowModel().rows.map(row => (
                <tr key={row.id} onClick={onRowClick ? () => onRowClick(row.original) : undefined} className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : undefined}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && onPaginationChange && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            {t('common.showingRange', { start: ((pagination.page - 1) * pagination.per_page) + 1, end: Math.min(pagination.page * pagination.per_page, pagination.total), total: pagination.total })}
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => onPaginationChange({...pagination, page: pagination.page - 1})} disabled={pagination.page <= 1} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">{t('common.pagination.previous')}</button>
            {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
              const p = Math.max(1, Math.min(pagination.page - 2, pagination.last_page - 4)) + i
              return (
                <button key={p} onClick={() => onPaginationChange({...pagination, page: p})} className={cn('w-8 h-8 text-xs rounded-lg border', p === pagination.page ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 hover:bg-gray-50')}>{p}</button>
              )
            })}
            <button onClick={() => onPaginationChange({...pagination, page: pagination.page + 1})} disabled={pagination.page >= pagination.last_page} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">{t('common.pagination.next')}</button>
          </div>
        </div>
      )}
    </div>
  )
}
